import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/store';
import { useChatbotPrompt } from '../../hooks/useChatBotPrompt';
import { getCheckInDataByYearAndMonth } from '../../api/dashboard';
import { getMoodStressLineData, getSatisfactionTirednessLineData } from '../../data/chartData';
import { getAllTimeTrendPrompts } from './prompts/userTrendPrompts';
import { moodStressOption, satisfactionTirednessOption } from '../../data/chartOptions';
import { AskWellnessBuddy } from './AskWellnessBuddy';
import ClipLoader from 'react-spinners/ClipLoader';
import { ChatBotContext } from '../../context/ChatBotContext';
import { Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    annotationPlugin,
);

const LineGraphsAllTime = ({ year, month }) => {
    const { updateChatBotPrompt } = useContext(ChatBotContext);

    const uid = useAuthStore(store => store.user?.uid);

    const checkInDataQuery = useQuery({
        queryKey: ['checkInByYearAndMonth', uid, year, month],
        queryFn: () => getCheckInDataByYearAndMonth(uid, year, month),
        retry: false,
        enabled: !(uid === null || uid === undefined),
    });

    const prompts = useChatbotPrompt(checkInDataQuery, getAllTimeTrendPrompts(checkInDataQuery));

    if (checkInDataQuery.isLoading) {
        return (
            <div className='loading col-span-2'>
                <ClipLoader
                    loading={checkInDataQuery.isLoading}
                    size={100}
                    aria-label='Loading Spinner'
                />
            </div>
        );
    }

    if (checkInDataQuery.isError || checkInDataQuery.data === null) {
        return (
            <div className='col-span-2 mt-3 text-center'>
                <p>You have not checked in yet. Please check in to view your mood and stress level summary.</p>
            </div>
        );
    }

    return (
        <>
            <div className='flex flex-column items-center'>
                <div className='min-h-[400px] w-full'>
                    <Line
                        options={moodStressOption}
                        data={getMoodStressLineData(checkInDataQuery.data?.mood, checkInDataQuery.data?.stress)}
                    />
                </div>
                <AskWellnessBuddy onClick={() => updateChatBotPrompt(prompts[0])}/>
            </div>
            <div className='flex flex-column items-center'>
                <div className='min-h-[400px] w-full'>
                    <Line
                        options={satisfactionTirednessOption}
                        data={getSatisfactionTirednessLineData(checkInDataQuery.data?.mood, {
                            satisfaction: checkInDataQuery.data?.satisfaction,
                            tiredness: checkInDataQuery.data?.tiredness,
                        })}
                    />
                </div>
                <AskWellnessBuddy onClick={() => updateChatBotPrompt(prompts[1])}/>
            </div>
        </>
    );
};

export default LineGraphsAllTime;
